import type { TrendRunPresentation, TrendRunPresentationStatus } from './types';

const STATUS_PRIORITY: Record<TrendRunPresentationStatus, number> = {
  latest_run_new: 4,
  latest_run_updated: 3,
  latest_run_reused: 2,
  older: 1,
};

export function getRunPresentationPriority(value?: TrendRunPresentation | null): number {
  return STATUS_PRIORITY[value?.status || 'older'] || 0;
}

export function getRunPresentationColor(value?: TrendRunPresentation | null): 'success' | 'info' | 'warning' | 'default' {
  if (value?.status === 'latest_run_new') return 'success';
  if (value?.status === 'latest_run_updated') return 'info';
  if (value?.status === 'latest_run_reused') return 'warning';
  return 'default';
}

export function getRunPresentationLabel(value?: TrendRunPresentation | null): string {
  return value?.label || 'Äldre underlag';
}

export function isLatestRunPresentation(value?: TrendRunPresentation | null): boolean {
  return value?.status === 'latest_run_new'
    || value?.status === 'latest_run_updated'
    || value?.status === 'latest_run_reused';
}

function dateScore(value?: string | null): number {
  if (!value) return 0;
  const time = new Date(value).getTime();
  return Number.isFinite(time) ? time : 0;
}

export function sortByRunPresentationThenDate<T extends {
  runPresentation?: TrendRunPresentation;
  createdAt?: string;
  updatedAt?: string;
  publishedAt?: string | null;
}>(items: T[]): T[] {
  return [...items].sort(compareByRunPresentationThenDate);
}

export function compareByRunPresentationThenDate<T extends {
  runPresentation?: TrendRunPresentation;
  createdAt?: string;
  updatedAt?: string;
  publishedAt?: string | null;
}>(left: T, right: T): number {
  return (
    getRunPresentationPriority(right.runPresentation) - getRunPresentationPriority(left.runPresentation)
    || dateScore(right.updatedAt || right.createdAt || right.publishedAt) - dateScore(left.updatedAt || left.createdAt || left.publishedAt)
  );
}

/**
 * Brand Radar sorting keeps severity as the primary key (most urgent first),
 * then falls back to run-presentation freshness and finally date. This avoids
 * burying high-severity hits just because they are not from the latest run.
 */
export function sortBySeverityThenRunPresentation<T extends {
  severityScore?: number;
  runPresentation?: TrendRunPresentation;
  createdAt?: string;
  updatedAt?: string;
  publishedAt?: string | null;
}>(items: T[]): T[] {
  return [...items].sort((left, right) => (
    (Number(right.severityScore) || 0) - (Number(left.severityScore) || 0)
    || compareByRunPresentationThenDate(left, right)
  ));
}
